import type { Entity, SnakeMeta } from '@api';
import type { QueryClient } from '@tanstack/react-query';
import { createEntityQueryOptions } from '@terminal/react-query-hooks/entities/useEntities.utils';
import { createSnakeMetaQueryOptions } from '@terminal/react-query-hooks/snakes/useSnake';
import { withGcTime } from '@terminal/services/query-client/utils';

interface ChartQueryOptions {
  queryClient: QueryClient;
  gcTime?: number;
}

interface FetchChartEntitiesProps extends ChartQueryOptions {
  tags: string[];
}

export const fetchChartEntities = async ({
  tags,
  queryClient,
  gcTime,
}: FetchChartEntitiesProps): Promise<Entity[]> => {
  const entities = await Promise.all(
    tags.map(async tag => {
      const queryOptions = withGcTime(createEntityQueryOptions(tag), gcTime);
      const response = await queryClient.ensureQueryData(queryOptions);

      if (!response.data) {
        queryClient.removeQueries({
          queryKey: queryOptions.queryKey,
          exact: true,
        });
        return undefined;
      }

      return response.data;
    })
  );

  return entities.filter((entity): entity is Entity => entity !== undefined);
};

interface FetchChartSnakeMetaProps extends ChartQueryOptions {
  snake: string;
}

export const fetchChartSnakeMeta = ({
  snake,
  queryClient,
  gcTime,
}: FetchChartSnakeMetaProps): Promise<SnakeMeta | undefined> =>
  queryClient
    .ensureQueryData(withGcTime(createSnakeMetaQueryOptions(snake), gcTime))
    .catch(() => undefined);
